import {
  amountRules,
  numberCalculatedViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { FC, useState, useCallback, useEffect, useMemo } from 'react';
import { OpmSampleWaterfallsDto } from '@app/shared/models/contracts/opm-calculation-results-dto';
import { useLocale } from '@app/core/hooks/useLocale';
import { translationKeys } from '@locale/setupI18n';
import { enumKeyByValue } from '@app/shared/helpers';
import { useAppDispatch, useAppSelector } from '@app/core/hooks/redux-hooks';
import { InstrumentType, OpmSimulatedValue } from '@app/shared/models/contracts/enums/shared-enums';
import { toPercentageUsingFormatter } from '@app/shared/formatters';
import ShareholderDebtSection from './SampleWaterfallOutputsShareholderDebtSections';
import OrdinaryEquitySection from './SampleWaterfallOutputsOrdinaryEquitySection';
import SampleWaterfallOutputsInterimCashFlowsSection from './SampleWaterfallOutputsInterimCashFlowsSection';
import SampleWaterfallOutputsSponsorReturnSection from './SampleWaterfallOutputsSponsorReturnSection';
import SampleWaterfallOutputsOtherCalculationsSection from './SampleWaterfallOutputsOtherCalculationsSection';
import { setSampleWaterfallTableExpandState } from '@app/core/store/ui-values-slice';
import useInstrumentSort from '@app/shared/hooks/useInstrumentSort';
import {
  getInstrumentsAcrossScenarios,
  getTrancheKeysAcrossScenarios,
  NOT_APPLICABLE,
} from './sample-waterfall-column-helpers';
import WaterfallValueCell from './WaterfallValueCell';

type ValueKeys = Omit<OpmSampleWaterfallsDto, 'tranches' | 'hasPayoutLogic' | 'instruments'>;

export interface ExpandedSectionsState {
  [key: string]: any;
  shareholderDebt?: boolean;
  ordinaryEquity?: boolean;
  interimCashFlows?: boolean;
  sponsorReturn?: boolean;
  otherCalculations?: boolean;
  ordinaryEquityInstruments?: { [instrumentId: string]: boolean };
  ordinaryEquityTranches?: { [trancheId: string]: boolean };
  interimCashFlowsInstruments?: { [instrumentId: string]: boolean };
  sponsorReturnInstruments?: { [instrumentId: string]: boolean }; // note, key can be instrumentId or 'Aggregate'
}

export enum ExpandableKeys {
  ShareholderDebt = 'shareholderDebt',
  OrdinaryEquity = 'ordinaryEquity',
  InterimCashFlows = 'interimCashFlows',
  SponsorReturn = 'sponsorReturn',
  OtherCalculations = 'otherCalculations',
  OrdinaryEquityInstruments = 'ordinaryEquityInstruments',
  OrdinaryEquityTranches = 'ordinaryEquityTranches',
  InterimCashFlowsInstruments = 'interimCashFlowsInstruments',
  SponsorReturnInstruments = 'sponsorReturnInstruments',
}

interface SampleWaterfallOutputsSummaryProps {
  waterfallResults: Dictionary<OpmSampleWaterfallsDto>;
  columnKeys: string[];
  exitDatesByColumn: Dictionary<string>;
}

const SampleWaterfallOutputsSummary: FC<SampleWaterfallOutputsSummaryProps> = ({
  waterfallResults,
  columnKeys,
  exitDatesByColumn,
}): JSX.Element => {
  const { l } = useLocale();
  const dispatch = useAppDispatch();
  const opmCalc = useAppSelector((state) => state.opmCalculation);
  const storedExpandableSectionsState = useAppSelector(
    (state) => state.uiValues.userSelections.sampleWaterfallTableExpandStates
  );

  // the rows that are required can differ between scenarios, so every instrument found in any
  // scenario has to be rendered, with the scenarios that do not use it rendered as empty cells
  const instrumentsAcrossScenarios = useMemo(
    () => getInstrumentsAcrossScenarios(waterfallResults, columnKeys),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [waterfallResults, columnKeys.join(',')]
  );

  const unsortedShareholderDebtInstruments = useMemo(
    () =>
      instrumentsAcrossScenarios.filter(
        (instrument) =>
          instrument.instrumentType !==
          enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity)
      ),
    [instrumentsAcrossScenarios]
  );

  const unsortedOrdinaryEquityInstruments = useMemo(
    () =>
      instrumentsAcrossScenarios.filter(
        (instrument) =>
          instrument.instrumentType ===
          enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity)
      ),
    [instrumentsAcrossScenarios]
  );

  const unsortedAllInstruments = useMemo(
    () => [...unsortedOrdinaryEquityInstruments, ...unsortedShareholderDebtInstruments],
    [unsortedOrdinaryEquityInstruments, unsortedShareholderDebtInstruments]
  );

  const shareholderDebtInstruments = useInstrumentSort(unsortedShareholderDebtInstruments);
  const ordinaryEquityInstruments = useInstrumentSort(unsortedOrdinaryEquityInstruments);
  const allInstruments = useInstrumentSort(unsortedAllInstruments);

  const getDefaultExpandedTranches = () => {
    const tranches: Record<string, boolean> = {};
    ordinaryEquityInstruments.forEach((instrument) => {
      getTrancheKeysAcrossScenarios(waterfallResults, columnKeys, instrument.instrumentId).forEach(
        (trancheKey) => {
          tranches[trancheKey] = true;
        }
      );
    });
    return tranches;
  };

  const getDefaultOrdinaryEquityExpandedInstruments = () => {
    const instruments: Record<string, boolean> = {};
    ordinaryEquityInstruments.forEach((instrument) => {
      instruments[instrument.instrumentId] = true;
    });
    return instruments;
  };

  const getDefaultAllExpandedInstruments = () => {
    const instruments: Record<string, boolean> = {};
    allInstruments.forEach((instrument) => {
      instruments[instrument.instrumentId] = true;
    });
    return instruments;
  };

  const defaultExpandedSections = useMemo(
    () =>
      storedExpandableSectionsState && Object.keys(storedExpandableSectionsState).length > 0
        ? storedExpandableSectionsState
        : {
            shareholderDebt: true,
            ordinaryEquity: true,
            interimCashFlows: true,
            sponsorReturn: true,
            otherCalculations: true,
            ordinaryEquityInstruments: getDefaultOrdinaryEquityExpandedInstruments(),
            ordinaryEquityTranches: getDefaultExpandedTranches(),
            interimCashFlowsInstruments: getDefaultAllExpandedInstruments(),
            sponsorReturnInstruments: {
              ...getDefaultOrdinaryEquityExpandedInstruments(),
              Aggregate: true,
            },
          },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storedExpandableSectionsState]
  );

  const [expandedSections, setExpandedSections] =
    useState<ExpandedSectionsState>(defaultExpandedSections);

  useEffect(() => {
    if (storedExpandableSectionsState && Object.keys(storedExpandableSectionsState).length > 0) {
      setExpandedSections(storedExpandableSectionsState);
    }
  }, [storedExpandableSectionsState]);

  useEffect(() => {
    dispatch(setSampleWaterfallTableExpandState(expandedSections));
  }, [expandedSections, dispatch]);

  useEffect(() => {
    if (!storedExpandableSectionsState || Object.keys(storedExpandableSectionsState).length === 0) {
      dispatch(setSampleWaterfallTableExpandState(defaultExpandedSections));
    }
  }, [storedExpandableSectionsState, dispatch, defaultExpandedSections]);

  const isEquitySimulation =
    opmCalc?.riskFreeValues?.inputs?.opmSimulatedValue ===
    enumKeyByValue(OpmSimulatedValue, OpmSimulatedValue.Equity);

  const createRow = (
    label: translationKeys,
    valueKey: keyof ValueKeys,
    isPercentage = false,
    className = '',
    decimalPoint: number | null = null
  ) => {
    return (
      <tr>
        <td className={className} data-testid={`${valueKey}-title`}>
          {l(label)}
        </td>
        {columnKeys.map((key) => {
          const currentResult = waterfallResults[key];
          const value = !currentResult
            ? NOT_APPLICABLE
            : isPercentage && typeof currentResult[valueKey] === 'number'
            ? toPercentageUsingFormatter(currentResult[valueKey] as number)
            : currentResult[valueKey] ?? 0;

          return (
            <WaterfallValueCell
              key={key}
              value={value}
              className={className}
              rules={{
                ...(isPercentage ? {} : amountRules),
                ...(decimalPoint !== null ? numberCalculatedViewRules : {}),
              }}
              normalize
            />
          );
        })}
      </tr>
    );
  };

  const handleToggle = useCallback((key: string, subKey?: string) => {
    setExpandedSections((prev) => {
      if (subKey) {
        return {
          ...prev,
          [key]: {
            ...prev[key],
            [subKey]: !prev[key]?.[subKey],
          },
        };
      }
      return { ...prev, [key]: !prev[key] };
    });
  }, []);

  return (
    <>
      <tbody>
        {createRow('_TimeToExit', 'exitYears', false, undefined, 2)}
        {createRow('_TransactionCost', 'transactionCostsPc', true)}
        {createRow('_IpoDiscount', 'ipoDiscountPc', true)}
        {createRow(l('_NetExitProceeds'), 'netExitProceeds')}
        {!isEquitySimulation && createRow('_NetDebtNegativeOrNetCash', 'totalNetCash')}
        {createRow('_TotalEquityTitle', 'totalEquityProceeds')}
        {shareholderDebtInstruments.length > 0 && (
          <ShareholderDebtSection
            waterfallResults={waterfallResults}
            columnKeys={columnKeys}
            shareholderDebtInstruments={shareholderDebtInstruments}
            onToggle={handleToggle}
            expandedSections={expandedSections}
          />
        )}
        {ordinaryEquityInstruments.length > 0 && (
          <OrdinaryEquitySection
            waterfallResults={waterfallResults}
            columnKeys={columnKeys}
            ordinaryEquityInstruments={ordinaryEquityInstruments}
            onToggle={handleToggle}
            expandedSections={expandedSections}
            exitDatesByColumn={exitDatesByColumn}
          />
        )}
        <SampleWaterfallOutputsInterimCashFlowsSection
          waterfallResults={waterfallResults}
          columnKeys={columnKeys}
          instruments={allInstruments}
          onToggle={handleToggle}
          expandedSections={expandedSections}
        />
        {ordinaryEquityInstruments.length > 0 && (
          <SampleWaterfallOutputsSponsorReturnSection
            waterfallResults={waterfallResults}
            columnKeys={columnKeys}
            allInstruments={allInstruments}
            onToggle={handleToggle}
            expandedSections={expandedSections}
          />
        )}
        <SampleWaterfallOutputsOtherCalculationsSection
          waterfallResults={waterfallResults}
          columnKeys={columnKeys}
          onToggle={handleToggle}
          expandedSections={expandedSections}
        />
      </tbody>
    </>
  );
};

export default SampleWaterfallOutputsSummary;
